import React, { useRef, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip as ChartTooltip, Legend } from 'chart.js';
import zoomPlugin from 'chartjs-plugin-zoom';
import NoDataMessage from './NoDataMessage';
import LoadingChart from './LoadingChart';
import { makeStyles } from "@mui/styles";
import { ExportButton } from './StrategyContent';
import { Box, Chip, Tooltip } from '@mui/material';
import { formatDrawdownDataForDisplay } from './utils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, ChartTooltip, Legend, zoomPlugin);

const useStyles = makeStyles(() => ({
  container: {
    height: '100%',
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  headerContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: '10px',
  },
  chartContainer: {
    flex: 1,
    position: 'relative',
    width: '100%',
    minHeight: '40vh',
    '@media (max-width: 600px)': {
      minHeight: '30vh',
    },
    '@media (min-width: 700px)': {
        minHeight: '28vh',
      },
    '@media (min-width: 1200px)': {
      minHeight: '28vh',
    },
  },
}));

const DrawDown = ({ data, isFullscreen, isLoading = false, forceDaily = false, showDataInfo = true }) => {
  const classes = useStyles();
  const chartRef = useRef(null);

  const processedData = useMemo(() => {
    if (!data || data.length === 0) return { processedPoints: [], isDaily: false, originalLength: 0, displayLength: 0 };

    const processed = formatDrawdownDataForDisplay(data, forceDaily, 999999);
    const isDaily = forceDaily || data.length <= 999999;

    return {
      processedPoints: processed,
      isDaily: isDaily,
      originalLength: data.length,
      displayLength: processed.length
    };
  }, [data, forceDaily]);

    useEffect(() => {
    const resizeChart = () => {
      if (chartRef.current) {
        chartRef.current.resize();
      }
    };

    resizeChart();
    window.addEventListener('resize', resizeChart);

    return () => {
      window.removeEventListener('resize', resizeChart);
    };
  }, [isFullscreen]);

  useEffect(() => {
        const handleDoubleClick = (event) => {
      event.preventDefault();
      if (chartRef.current) {
        try {
          chartRef.current.resetZoom('default');
        } catch (error) {
          // Alternative method if resetZoom doesn't work
          if (chartRef.current.options?.plugins?.zoom) {
            chartRef.current.options.plugins.zoom.zoom.wheel.enabled = false;
            chartRef.current.options.plugins.zoom.zoom.wheel.enabled = true;
            chartRef.current.update('none');
          }
        }
      }
    };

    // Add delay to ensure chart is fully rendered
    const timer = setTimeout(() => {
      const chartCanvas = chartRef.current?.canvas;
      if (chartCanvas) {
        chartCanvas.addEventListener('dblclick', handleDoubleClick);

        // Store reference for cleanup
        chartCanvas._doubleClickHandler = handleDoubleClick;
      }
    }, 100);

    return () => {
      clearTimeout(timer);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const chartCanvas = chartRef.current?.canvas;
      if (chartCanvas && chartCanvas._doubleClickHandler) {
        chartCanvas.removeEventListener('dblclick', chartCanvas._doubleClickHandler);
        delete chartCanvas._doubleClickHandler;
      }
    };
  }, [data]);

  if (isLoading) {
    return <LoadingChart message="Loading downside risk chart..." />;
  }

  if (!data || data.length === 0) {
    return <NoDataMessage message="Please click calculate button on settings." />;
  }

  const { processedPoints, isDaily, originalLength, displayLength } = processedData;

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      x: {
        title: {
          display: true,
          text: 'Date',
          font: {
            size: 16,
          },
        },

      },
      y: {
        beginAtZero: false,
        title: {
          display: true,
          text: 'Downside Risk %',
          font: {
            size: 16,
          },
        },
        ticks: {
          callback: function(value) {
            return value.toFixed(2);
          }
        }
      }
    },
    plugins: {
      legend: {
        display: false
      },
      title: {
        display: true,
        text: ''
      },
      subtitle: {
        display: true,
        text: 'Mouse wheel: zoom timeline | Drag: pan timeline | Double-click: reset zoom',
        font: {
          size: 12,
          style: 'italic'
        },
        color: '#666',
        padding: {
          bottom: 10
        }
      },
      tooltip: {
        callbacks: {
          title: function (context) {
            return `Date: ${context[0].label}`;
          },
          label: function (context) {
            const value = Math.abs(context.raw);
            return `Drawdown: ${value.toFixed(2)}%`;
          },
        //   afterLabel: function (context) {
        //     const value = Math.abs(context.raw);
        //     if (value > 20) {
        //       return '⚠️ High drawdown period';
        //     } else if (value > 10) {
        //       return '⚡ Moderate drawdown';
        //     } else if (value > 0) {
        //       return '📉 Minor drawdown';
        //     }
        //     return '';
        //   }
        }
      },
      zoom: {
        zoom: {
          wheel: { enabled: true },
          pinch: { enabled: true },
          mode: "x",
        },
        pan: {
          enabled: true,
          mode: "x",
        },
        limits: {
          x: { min: 'original', max: 'original' }
        },
      },
    }
      };

  const chartData = {
    labels: processedPoints.map(point => {
      if (isDaily) {
        const date = new Date(point.date);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: originalLength > 365 ? 'numeric' : undefined
        });
      } else {
        return point.monthYear;
      }
    }),
    datasets: [
      {
        label: 'DrawDown',
        data: processedPoints.map(point => point.drawdown * 100),
        borderColor: 'rgba(75, 192, 192, 1)',
        borderWidth: 2,
        fill: false,
        tension: 0.3,
        pointRadius: originalLength > 365 ? 0 : 1,
        pointHoverRadius: 3,
      }
    ]
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headerContainer} style={{ justifyContent: 'space-between', alignItems: 'center' }}>
        {showDataInfo && originalLength !== displayLength && (
          <Box style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Tooltip title={`Showing ${displayLength} points from ${originalLength} total data points for better performance`}>
              <Chip
                label={`${displayLength}/${originalLength} points`}
                size="small"
                variant="outlined"
                color="info"
              />
            </Tooltip>
          </Box>
        )}
        <ExportButton chartId="drawdownChart" />
      </Box>
      <Box className={classes.chartContainer}>
        <div id="drawdownChart" style={{ height: '100%', width: '100%' }}>
          <Line
            data={chartData}
            options={options}
            ref={chartRef}
          />
        </div>
      </Box>
    </Box>
  );
};

export default DrawDown;